import RenderManager from '../rendering/render-manager';
import { fire } from '../utils/events';
import saveState from '../utils/object-rotation-controls/save-state';

class ObjectRotationControlsCoobo {
    alwaysSaveState = Q3.editMode && !Q3.assetMode;

    constructor(controlsDom, controls) {
        this._controlsDom = controlsDom;
        this._controls = controls;

        this._enabled = false;
        this._events = new THREE.EventDispatcher();

        this._onGrabThis = this._onGrab.bind(this);
        this._onReleaseThis = this._onRelease.bind(this);
        this._onDragStartThis = this._onDragStart.bind(this);
        this._onDragStopThis = this._onDragStop.bind(this);
        this._onDragThis = this._onDrag.bind(this);
        this._onExecuteThis = this._onExecute.bind(this);
        this._onChangeThis = this._onChange.bind(this);

        this._applyObjects = null;
        this._keepApplyObjects = null;
        this._wasSelectedOnGrab = null;
    }

    get events() {
        return this._events;
    }

    set enabled(v) {
        v = Boolean(v);
        if (v === this._enabled) return;

        this._enabled = v;

        this._controls.enabled = v;
        this._controlsDom.enabled = v;

        const ctrlEvt = this._controls.events;

        if (v) {
            this._applyObjects = new Set();
            this._keepApplyObjects = new Set();
            this._wasSelectedOnGrab = new Set();

            ctrlEvt.addEventListener("grab", this._onGrabThis);
            ctrlEvt.addEventListener("release", this._onReleaseThis);
            ctrlEvt.addEventListener("dragstart", this._onDragStartThis);
            ctrlEvt.addEventListener("dragstop", this._onDragStopThis);
            ctrlEvt.addEventListener("drag", this._onDragThis);
            ctrlEvt.addEventListener("execute", this._onExecuteThis);
            ctrlEvt.addEventListener("change", this._onChangeThis);
        } else {
            ctrlEvt.removeEventListener("grab", this._onGrabThis);
            ctrlEvt.removeEventListener("release", this._onReleaseThis);
            ctrlEvt.removeEventListener("dragstart", this._onDragStartThis);
            ctrlEvt.removeEventListener("dragstop", this._onDragStopThis);
            ctrlEvt.removeEventListener("drag", this._onDragThis);
            ctrlEvt.removeEventListener("execute", this._onExecuteThis);
            ctrlEvt.removeEventListener("change", this._onChangeThis);

            this._applyObjects = null;
            this._keepApplyObjects = null;
            this._wasSelectedOnGrab = null;
        }
    }

    get enabled() {
        return this._controls.enabled;
    }

    get rotationMode() { return this._controls.rotationMode; }
    set rotationMode(v) { this._controls.rotationMode = v; }

    get object() { return this._controls.object; }
    get target() { return Q3.physicalCameraControls.target; }

    setObjectSelector(selectObject) { return this._controls.setObjectSelector(selectObject); }
    setDefaultRotationGetter(getObjectDefaultRotation) { return this._controls.setDefaultRotationGetter(getObjectDefaultRotation); }
    showSnackBar(message) { return this._controls.showSnackBar(message); }

    isSelected(object) {
        if (!Q3.selected || !Q3.selected.isObject3D || !this.alwaysSaveState) return false;

        const objects = [Q3.selected];

        do {
            const next = objects.shift();

            if (next === object) return true;

            objects.push(...next.children);

        } while (objects.length > 0)

        return false;
    }

    update() {
        if (this._enabled && !Q3.isTransitioning && !Q3.assigningLine) {
            this._controls.update();
            this._controlsDom.clearApply();

            for (const object of this._applyObjects) {
                if (!this._keepApplyObjects.has(object)) {
                    this._applyObjects.delete(object);
                }
            }

            this._keepApplyObjects.clear();
        }
    }

    clearObjectRotationSpaces() { return this._controls.clearObjectRotationSpaces(); }

    isDamping() { return Q3.physicalCameraControls.isDamping(); }
    setPanLimits() { return Q3.physicalCameraControls.setPanLimits(...arguments); }
    cancelDamping() { return Q3.physicalCameraControls.cancelDamping(); }
    setAzimuthalAngle() { return Q3.physicalCameraControls.setAzimuthalAngle(...arguments); }
    setPolarAngle() { return Q3.physicalCameraControls.setPolarAngle(...arguments); }

    get minAzimuthAngle() { return Q3.physicalCameraControls.minAzimuthAngle; }
    set minAzimuthAngle(value) { Q3.physicalCameraControls.minAzimuthAngle = value; }

    get maxAzimuthAngle() { return Q3.physicalCameraControls.maxAzimuthAngle; }
    set maxAzimuthAngle(value) { Q3.physicalCameraControls.maxAzimuthAngle = value; }

    get minDistance() { return Q3.physicalCameraControls.minDistance; }
    set minDistance(value) { Q3.physicalCameraControls.minDistance = value; }

    get maxDistance() { return Q3.physicalCameraControls.maxDistance; }
    set maxDistance(value) { Q3.physicalCameraControls.maxDistance = value; }

    get minPolarAngle() { return Q3.physicalCameraControls.minPolarAngle; }
    set minPolarAngle(value) { Q3.physicalCameraControls.minPolarAngle = value; }

    get maxPolarAngle() { return Q3.physicalCameraControls.maxPolarAngle; }
    set maxPolarAngle(value) { Q3.physicalCameraControls.maxPolarAngle = value; }

    setAutoSpin(object, enabled, speed) { return this._controls.setAutoSpin(object, enabled, speed); }

    _onGrab({ type, action, object }) {
        const isSelected = this.isSelected(object);

        if (isSelected) {
            this._wasSelectedOnGrab.add(object);
        }

        this._events.dispatchEvent({ type, action, object });

        if (isSelected) fire("start_transform");
    }

    _onRelease({ type, action, object }) {
        const apply = this._controlsDom.apply || this.isSelected(object);

        if (apply) {
            this._applyObjects.add(object);
        } else {
            this._applyObjects.delete(object);
        }

        const wasSelected = this._wasSelectedOnGrab.has(object);
        const isSelected = this.isSelected(object);
        this._wasSelectedOnGrab.delete(object);

        this._events.dispatchEvent({ type, action, object, apply });

        if (wasSelected && isSelected) {
            fire("finish_transform");

            /*
             * This looked like dead code in ObjectRotationControls,
             * where it was taken from, because this._dragging was
             * assigned null above this code in the same function
             */
            if (Q3.editMode && /*this._dragging*/ false) {
                const activeSlide = SlideManager.activeSlide;
                const cameraLimits = LimitManager.getObjectCameraLimits(activeSlide, this._dragging.object.uuid);
                if (!cameraLimits || !cameraLimits.panLimitEnabled)
                    this.showSnackBar("Transformation saved");
            }
        }
    }

    _onDragStart({ type, action, object }) {
        this._events.dispatchEvent({ type, action, object });
    }

    _onDragStop({ type, action, object }) {
        const apply = this._controlsDom.apply || this.isSelected(object);

        if (apply) {
            this._applyObjects.add(object);
        } else {
            this._applyObjects.delete(object);
        }

        this._events.dispatchEvent({ type, action, object, apply });
    }

    _onDrag({ type, action, object }) {
        const apply = this._controlsDom.apply || this.isSelected(object);

        if (apply) {
            this._applyObjects.add(object);
        } else {
            this._applyObjects.delete(object);
        }

        this._events.dispatchEvent({ type, action, object });
    }

    _onExecute({ type, action, object }) {
        const apply = this._controlsDom.apply || this.isSelected(object);

        if (apply) {
            this._applyObjects.add(object);
        } else {
            this._applyObjects.delete(object);
        }

        this._events.dispatchEvent({ type, action, object, apply });
    }

    _onChange({ type, action, object }) {
        RenderManager.activeCamera.update = true;

        const apply = this._applyObjects.has(object);

        if (apply) {
            this._keepApplyObjects.add(object);

            saveState(object);
        }

        this._events.dispatchEvent({ type, action, object, apply });
    }
}

export default ObjectRotationControlsCoobo;
export { ObjectRotationControlsCoobo };
